import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Bell, ArrowLeft, Shield, User, Globe, MapPin, Clock, 
  Wrench, Check, Trash2, Filter, ChevronDown 
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { getAuthHeaders } from "@/utils/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SCANNER_ICONS = {
  police: Shield,
  user: User,
  public: Globe,
  system: Bell
};

const SCANNER_COLORS = {
  police: { bg: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/30" },
  user: { bg: "bg-yellow-500/10", text: "text-yellow-400", border: "border-yellow-500/30" },
  public: { bg: "bg-green-500/10", text: "text-green-400", border: "border-green-500/30" },
  system: { bg: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/30" }
};

const SCANNER_LABELS = {
  police: "Police Authority",
  user: "Registered User",
  public: "Public",
  system: "Proximity Alert"
};

export default function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({ total_scans: 0, police_scans: 0, user_scans: 0, public_scans: 0, proximity_alerts: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const fetchNotifications = async (reset = false) => {
    try {
      setLoading(true);
      const skip = reset ? 0 : page * 20;
      const response = await fetch(`${API}/notifications/history?limit=20&skip=${skip}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        if (reset) {
          setNotifications(data.notifications || []);
        } else {
          setNotifications(prev => [...prev, ...(data.notifications || [])]);
        }
        setStats(data.stats || { total_scans: 0, police_scans: 0, user_scans: 0, public_scans: 0, proximity_alerts: 0 });
        setHasMore((data.notifications || []).length === 20);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications(true);
  }, []);

  const loadMore = () => {
    setPage(prev => prev + 1);
    fetchNotifications();
  };

  const markAsRead = async (notificationId) => {
    try {
      const response = await fetch(`${API}/notifications/${notificationId}/read`, {
        method: "PUT",
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setNotifications(prev => 
          prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
        );
      }
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const response = await fetch(`${API}/notifications/${notificationId}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
        toast.success("Notification deleted");
      }
    } catch (error) {
      toast.error("Failed to delete notification");
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await fetch(`${API}/notifications/read-all`, {
        method: "PUT",
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        toast.success("All notifications marked as read");
      }
    } catch (error) {
      toast.error("Failed to mark all as read");
    }
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === "all") return true;
    return n.scanner_type === filter;
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3 lg:px-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              <span className="font-heading text-lg uppercase tracking-wider">Notification History</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={markAllAsRead}
                data-testid="mark-all-read-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                Mark all read
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-5xl mx-auto">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="border-border bg-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">Total</p>
                  <p className="text-2xl font-heading font-bold">{stats.total_scans}</p>
                </div>
                <Bell className="w-6 h-6 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className={`border-border bg-card ${SCANNER_COLORS.police.border}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">Police</p>
                  <p className={`text-2xl font-heading font-bold ${SCANNER_COLORS.police.text}`}>{stats.police_scans}</p>
                </div>
                <Shield className={`w-6 h-6 ${SCANNER_COLORS.police.text} opacity-50`} />
              </div>
            </CardContent>
          </Card>
          <Card className={`border-border bg-card ${SCANNER_COLORS.user.border}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">Users</p>
                  <p className={`text-2xl font-heading font-bold ${SCANNER_COLORS.user.text}`}>{stats.user_scans}</p>
                </div>
                <User className={`w-6 h-6 ${SCANNER_COLORS.user.text} opacity-50`} />
              </div>
            </CardContent>
          </Card>
          <Card className={`border-border bg-card ${SCANNER_COLORS.public.border}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">Public</p>
                  <p className={`text-2xl font-heading font-bold ${SCANNER_COLORS.public.text}`}>{stats.public_scans}</p>
                </div>
                <Globe className={`w-6 h-6 ${SCANNER_COLORS.public.text} opacity-50`} />
              </div>
            </CardContent>
          </Card>
          <Card className={`border-border bg-card ${SCANNER_COLORS.system.border} col-span-2 md:col-span-1`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">Nearby</p>
                  <p className={`text-2xl font-heading font-bold ${SCANNER_COLORS.system.text}`}>{stats.proximity_alerts}</p>
                </div>
                <Bell className={`w-6 h-6 ${SCANNER_COLORS.system.text} opacity-50`} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-lg uppercase tracking-wide text-muted-foreground">
            Scan History ({filteredNotifications.length})
          </h2>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="filter-btn">
                <Filter className="w-4 h-4 mr-2" />
                {filter === "all" ? "All Types" : SCANNER_LABELS[filter]}
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setFilter("all")}>All Types</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter("police")}>
                <Shield className="w-4 h-4 mr-2 text-blue-400" />
                Police Authority
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter("user")}>
                <User className="w-4 h-4 mr-2 text-yellow-400" />
                Registered Users
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter("public")}>
                <Globe className="w-4 h-4 mr-2 text-green-400" />
                Public
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter("system")}>
                <Bell className="w-4 h-4 mr-2 text-orange-400" />
                Proximity Alerts
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Notification List */}
        {loading && notifications.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading notifications...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="py-12 text-center">
              <Bell className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="font-heading text-xl uppercase mb-2">No Notifications</h3>
              <p className="text-muted-foreground">
                {filter === "all" 
                  ? "You'll be notified when your stolen tools are scanned by anyone."
                  : `No scans from ${SCANNER_LABELS[filter].toLowerCase()} yet.`
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredNotifications.map((notification) => {
              const Icon = SCANNER_ICONS[notification.scanner_type] || Globe;
              const colors = SCANNER_COLORS[notification.scanner_type] || SCANNER_COLORS.public;
              
              return (
                <Card 
                  key={notification.notification_id} 
                  className={`border-border bg-card overflow-hidden ${!notification.is_read ? "border-primary/30" : ""}`}
                  data-testid={`notification-card-${notification.notification_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      {/* Icon */}
                      <div className={`p-3 rounded-lg ${colors.bg} flex-shrink-0`}>
                        <Icon className={`w-6 h-6 ${colors.text}`} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">{notification.title}</h3>
                              {!notification.is_read && (
                                <Badge className="bg-primary text-primary-foreground text-xs">NEW</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{notification.message}</p>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {!notification.is_read && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => markAsRead(notification.notification_id)}
                                className="h-8 w-8 p-0"
                                data-testid={`mark-read-${notification.notification_id}`}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => deleteNotification(notification.notification_id)}
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                              data-testid={`delete-${notification.notification_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Tool Info */}
                        {notification.tool_info && (
                          <div className="mt-3 p-3 bg-muted/30 rounded-lg">
                            <div className="flex items-center gap-2 text-sm">
                              <Wrench className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">{notification.tool_info.manufacturer}</span>
                              <span className="text-muted-foreground">{notification.tool_info.tool_type}</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 font-mono">
                              S/N: {notification.tool_info.serial_number}
                            </p>
                          </div>
                        )}

                        {/* Meta Info */}
                        <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDateTime(notification.created_at)}
                          </span>
                          
                          <Badge variant="outline" className={`${colors.border} ${colors.text} text-xs`}>
                            {SCANNER_LABELS[notification.scanner_type]}
                          </Badge>
                          
                          {notification.scanner_type === "police" && notification.scanner_info?.authority_name && (
                            <span className="flex items-center gap-1">
                              <Shield className="w-3 h-3" />
                              {notification.scanner_info.authority_name}
                            </span>
                          )}
                          
                          {notification.location && (notification.location.latitude || notification.location.lat) && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {(notification.location.latitude || notification.location.lat).toFixed(4)}, {(notification.location.longitude || notification.location.lon).toFixed(4)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Load More */}
            {hasMore && (
              <div className="text-center pt-4">
                <Button 
                  variant="outline" 
                  onClick={loadMore} 
                  disabled={loading}
                  data-testid="load-more-btn"
                >
                  {loading ? "Loading..." : "Load More"}
                </Button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
